import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Loader2, Save, Clock, Users, History, CheckCircle2, XCircle, UserCog, Send, CalendarClock, ChevronDown, Download, RefreshCw, AlertTriangle, Eye, MailCheck, Megaphone } from "lucide-react";

// Elenco dei funnel presenti nel software (riepilogo, sola consultazione).
const FUNNELS = [
  { key: "partner", nome: "Funnel Partner", pagina: "Portafoglio Partner",
    descr: "Benvenuto e solleciti automatici (saldo Vesta, inattività, contratto in scadenza/scaduto).",
    mittente: "Andrea Natalia · andrea.natalia@torrisolare.it",
    destinatari: "Partner (email azienda)",
    cc: "Agente di competenza + Head of Partner" },
  { key: "visite", nome: "Funnel Visite", pagina: "Agenda",
    descr: "Report delle visite commerciali e rimborsi km alla Direzione.",
    mittente: "Michele Torri · michele@torrisolare.it" },
  { key: "affidamenti", nome: "Funnel Affidamenti", pagina: "Affidamenti",
    descr: "Esposizioni, clienti fuori affidamento e allerta bilanci mancanti.",
    mittente: "Michele Torri · michele@torrisolare.it" },
  { key: "agenzie", nome: "Funnel Agenzie — Impegni & KPI", pagina: "Agenzie",
    descr: "Impegni di chiusura e KPI periodici inviati a ciascuna agenzia.",
    mittente: "Andrea Natalia · andrea.natalia@torrisolare.it",
    destinatari: "Agenti dell'agenzia" },
  { key: "commesse", nome: "Funnel Commesse", pagina: "Commesse", perRow: true,
    descr: "Promemoria ritiro/consegna e pagamento, attivabile per singola commessa.",
    mittente: "Valeria Ghignatti",
    destinatari: "Cliente/partner della commessa",
    cc: "Agenzia + Responsabili Commerciali + Back Office",
    oggetto: "Commessa {n} – promemoria dal Back Office" },
  { key: "magazzino", nome: "Funnel Magazzino", pagina: "Stima magazzino",
    descr: "Report settimanale del magazzino (fiscale CSV e articoli a listino) con proiezione al 31/12; allega il PDF riunione pianificazioni (forecast 12 mesi) e il magazzino produzione al 31/12.",
    mittente: "Michele Torri · michele@torrisolare.it" },
];

const TIPO_LABELS = {
  daily_reminders: "Promemoria giornaliero",
  weekly_report: "Report settimanale",
  agent_activity_todo: "To-do agenti",
  agent_activity_done: "Riepilogo attività svolte",
  michele_ai_daily: "Report Direzionale (Dashboard Michele)",
  responsabile_ai_report: "Report Responsabile AI",
  agency_ai_report: "Valutazione Agenzia AI",
  welcome_partner: "Funnel Partner #1 · Benvenuto",
  partner_funnel_vesta: "Funnel Partner #2 · Saldo Vesta",
  partner_funnel_inattivo: "Funnel Partner #3 · Inattività",
  partner_funnel_scadenza: "Funnel Partner #4 · Contratto in scadenza",
  partner_funnel_scaduto: "Funnel Partner #5 · Contratto scaduto",
  newsletter: "Newsletter",
  prenota: "Prenotazione Catalogo Vesta",
  prenota_annullata: "Prenotazione annullata",
  backup_notturno: "Backup notturno",
  backup_fallito: "Backup notturno non riuscito",
  report_scoperti: "Report Articoli Scoperti (giornaliero)",
};
const tipoLabel = (t) => {
  const base = TIPO_LABELS[t] || t || "—";
  return MAIL_NUM[t] ? `#${MAIL_NUM[t]} · ${base}` : base;
};
const fmtT = (t) => (t ? new Date(t).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

// Numero identificativo fisso di ogni mail automatica (stabile per chiave, coerente in ogni schermata)
const MAIL_NUM = { michele_ai_daily: 1, responsabile_ai_report: 2, agency_ai_report: 3, daily_reminders: 4, weekly_report: 5, agent_activity_todo: 6 };
const mailTitle = (s) => (MAIL_NUM[s.key] ? `#${MAIL_NUM[s.key]} · ${s.nome}` : s.nome);

const deliveryCell = (l) => {
  if (l.status !== "sent") return <span className="text-muted-foreground">—</span>;
  if (l.complained_at) return <span className="inline-flex items-center gap-1 text-red-400" title={`Segnalata come spam · ${fmtT(l.complained_at)}`}>Reclamo</span>;
  if (l.bounced_at) return <span className="inline-flex items-center gap-1 text-red-400" title={`Non recapitata · ${fmtT(l.bounced_at)}`}>Rimbalzata</span>;
  if (l.opened_at) return <span className="inline-flex items-center gap-1 text-emerald-300" data-testid={`log-opened-${l.id}`} title={`Ultima apertura ${fmtT(l.last_open_at || l.opened_at)}`}><Eye className="h-3.5 w-3.5" /> Aperta {fmtT(l.opened_at)}{l.open_count > 1 ? ` (${l.open_count}x)` : ""}</span>;
  if (l.delivered_at) return <span className="inline-flex items-center gap-1 text-green-400" data-testid={`log-delivered-${l.id}`} title={`Consegnata in casella · ${fmtT(l.delivered_at)}`}><MailCheck className="h-3.5 w-3.5" /> Consegnata {fmtT(l.delivered_at)}</span>;
  return <span className="text-amber-300/80" title="Inviata al provider, in attesa di conferma di consegna">In consegna…</span>;
};

const schedStatus = (s) => {
  switch (s.last_status) {
    case "ok": return { cls: "text-green-400", Icon: CheckCircle2, text: `Ultimo invio eseguito${s.last_executed_at ? " · " + fmtT(s.last_executed_at) : ""}` };
    case "retrying": return { cls: "text-amber-400", Icon: Clock, text: "Riprogrammato: nuovo tentativo automatico entro ~1h" };
    case "error": return { cls: "text-red-400", Icon: XCircle, text: `Errore: non inviato dopo i tentativi (atteso ${fmtT(s.last_due)})` };
    default: return { cls: "text-muted-foreground", Icon: Clock, text: "In attesa (finestra in corso)" };
  }
};

const errMotivo = (e) => {
  if (!e) return "Errore sconosciuto";
  const s = String(e);
  if (s.includes("429") || s.includes("422")) return "Limite invii raggiunto (rate limit) — verrà ritentato automaticamente";
  if (/\b40[13]\b/.test(s)) return "Autorizzazione email non valida";
  if (/\b50[0-9]\b/.test(s)) return "Servizio email temporaneamente non disponibile";
  if (/timeout|timed out/i.test(s)) return "Timeout invio — ritentato";
  if (/\bG2\b|\bG3\b/.test(s)) return "Bloccata dal filtro sicurezza email";
  return s.slice(0, 140);
};

export default function MailAutomatiche() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState(null);
  const [agents, setAgents] = useState(null);
  const [testKey, setTestKey] = useState(null);
  const [testTarget, setTestTarget] = useState({});
  const [schedule, setSchedule] = useState(null);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [resending, setResending] = useState(false);
  const [runningKey, setRunningKey] = useState(null);
  const [funnelStatus, setFunnelStatus] = useState(null);

  const loadFunnels = () => Promise.allSettled([
    api.get("/partner-funnel-config"),
    api.get("/visite-funnel-config"),
    api.get("/affidamenti-funnel-config"),
    api.get("/agency-funnel-config"),
    api.get("/magazzino-funnel-config"),
  ]).then(([p, v, a, ag, mg]) => {
    const roleLbl = { admin: "Admin", commercial_manager: "Resp. Commerciale", head_of_partner: "Head of Partner", back_office: "Back Office" };
    const pd = p.status === "fulfilled" ? p.value.data : null;
    const vd = v.status === "fulfilled" ? (v.value.data?.config || {}) : null;
    const ad = a.status === "fulfilled" ? (a.value.data?.config || {}) : null;
    const gd = ag.status === "fulfilled" ? ag.value.data : null;
    const md = mg.status === "fulfilled" ? (mg.value.data?.config || {}) : null;
    setFunnelStatus({
      partner: pd ? { enabled: !!pd.attivo_globale, oggetto: ["welcome", "vesta", "inattivo", "scadenza", "scaduto"].map((k) => pd[k]?.subject).filter(Boolean).join(" · ") } : null,
      visite: vd ? { enabled: !!vd.enabled, destinatari: (vd.recipients || []).join(", "), cc: (vd.cc || []).join(", "), oggetto: vd.subject } : null,
      affidamenti: ad ? { enabled: !!ad.enabled, destinatari: (ad.recipients || []).join(", "), cc: (ad.cc || []).join(", "), oggetto: ad.subject } : null,
      agenzie: gd ? { enabled: !!gd.attivo, cc: [...(gd.cc_roles || []).map((r) => roleLbl[r] || r), ...(gd.cc_emails || [])].join(", "), oggetto: gd.subject } : null,
      magazzino: md ? { enabled: !!md.enabled, destinatari: (md.recipients || []).join(", "), cc: (md.cc || []).join(", "), oggetto: md.subject } : null,
    });
  });

  const loadSchedule = () => api.get("/email-schedule").then((r) => setSchedule(r.data.items)).catch(() => {});

  const runNow = async (key) => {
    setRunningKey(key);
    try {
      await api.post("/email-schedule/run-now", { key });
      toast.success("Invio avviato — controlla lo Storico tra 1-2 minuti (usa i dati correnti)");
      setTimeout(() => { loadLogs(); loadSchedule(); }, 5000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setRunningKey(null); }
  };

  const resendFailed = async () => {
    setResending(true);
    try {
      const r = await api.post("/email-logs/resend-failed");
      const n = r.data?.pending ?? 0;
      toast.success(n > 0 ? `Reinvio avviato per ${n} destinatari falliti — controlla lo storico tra 1-2 minuti` : "Nessuna email fallita da reinviare oggi ✅");
      setTimeout(loadLogs, 4000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reinvio non riuscito"); }
    finally { setResending(false); }
  };

  const sendTest = async (key) => {
    setTestKey(key);
    try {
      const asUser = testTarget[key] || "";
      const body = { key };
      if (asUser) body.as_user = asUser;
      const r = await api.post("/email-test-send", body);
      const chi = asUser ? (agents?.find((a) => a.id === asUser)?.name || "utente selezionato") : "Direzione (vista rete)";
      if (r.data.status === "queued") toast.success(`Prova per ${chi} in generazione: arriverà a ${r.data.to} tra 1-2 minuti`);
      else toast.success(`Email di prova (${chi}) inviata a ${r.data.to}`);
      loadLogs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setTestKey(null); }
  };

  const load = () => api.get("/email-settings").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore"));
  const loadLogs = () => api.get("/email-logs?limit=200").then((r) => setLogs(r.data)).catch(() => {});
  const loadAgents = () => api.get("/email-agent-prefs").then((r) => setAgents(r.data.agents)).catch(() => {});
  useEffect(() => { load(); loadLogs(); loadAgents(); loadSchedule(); loadFunnels(); }, []);

  const st = data?.settings;
  const setTpl = (key, field, val) => setData((d) => ({ ...d, settings: { ...d.settings, templates: { ...d.settings.templates, [key]: { ...d.settings.templates[key], [field]: val } } } }));

  const ROLE_OPTS = [["admin", "Admin"], ["commercial_manager", "Responsabile Commerciale"], ["head_of_partner", "Head of Partner"], ["back_office", "Back Office"]];
  const DOW_OPTS = [[0, "Lunedì"], [1, "Martedì"], [2, "Mercoledì"], [3, "Giovedì"], [4, "Venerdì"], [5, "Sabato"], [6, "Domenica"]];
  const CARD_ORDER = ["michele_ai_daily", "responsabile_ai_report", "agency_ai_report", "daily_reminders", "weekly_report", "agent_activity_todo"];
  const orderedCatalog = [...(data?.catalog || [])].sort((a, b) => {
    const ia = CARD_ORDER.indexOf(a.key), ib = CARD_ORDER.indexOf(b.key);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
  const setRecip = (key, patch) => {
    const cur = st.templates[key].recipients || { roles: [], agencies_on: false, agencies: [], emails: [] };
    setTpl(key, "recipients", { roles: cur.roles || [], agencies_on: cur.agencies_on || false, agencies: cur.agencies || [], manager_personal: cur.manager_personal || false, emails: cur.emails || [], ...patch });
  };
  const toggleRole = (key, role) => {
    const cur = (st.templates[key].recipients?.roles) || [];
    setRecip(key, { roles: cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role] });
  };
  const [openAgencies, setOpenAgencies] = useState({});
  const [showRecap, setShowRecap] = useState(false);
  const [recapPdfBusy, setRecapPdfBusy] = useState(false);

  const downloadRecapPdf = async () => {
    setRecapPdfBusy(true);
    try {
      const r = await api.get("/email-settings/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "mail-automatiche-torri-solar.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download PDF non riuscito"); }
    finally { setRecapPdfBusy(false); }
  };
  const allAgencyIds = (data?.agencies || []).map((a) => a.id);
  const agencyChecked = (key, id) => {
    const a = st.templates[key].recipients?.agencies;
    return !a || a.length === 0 ? true : a.includes(id);
  };
  const toggleAgency = (key, id) => {
    const a = st.templates[key].recipients?.agencies;
    let cur = a && a.length ? [...a] : [...allAgencyIds];
    cur = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setRecip(key, { agencies: cur.length === allAgencyIds.length ? [] : cur });
  };
  const [emailText, setEmailText] = useState({});
  const recipEmailsValue = (key) => (emailText[key] !== undefined ? emailText[key] : ((st.templates[key].recipients?.emails) || []).join(", "));
  const commitEmails = (key) => {
    const val = emailText[key];
    if (val === undefined) return;
    setRecip(key, { emails: val.split(",").map((s) => s.trim()).filter(Boolean) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/email-settings", { from_name: st.from_name, reply_to: st.reply_to, templates: st.templates });
      setData((d) => ({ ...d, settings: r.data }));
      toast.success("Impostazioni email salvate");
      loadSchedule();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="flex flex-col gap-6" data-testid="mail-automatiche-page">
      <div className="order-1 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-black tracking-tight"><Mail className="h-7 w-7 text-primary" /> Mail Automatiche</h1>
          <p className="text-sm text-muted-foreground">Testo, mittente, destinatari, frequenza e attivazione di ogni email automatica inviata dal sistema. Nome mittente e reply-to si impostano per ogni singola mail (l'indirizzo email del mittente è gestito dalla piattaforma e non è modificabile).</p>
        </div>
        <Button onClick={save} disabled={busy} data-testid="save-email-settings-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva</Button>
      </div>

      <Card className="order-2 p-5" data-testid="funnels-overview-card">
        <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold"><Megaphone className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /> Funnel del software</h2>
        <p className="mb-4 text-[11px] text-muted-foreground">Riepilogo dei funnel automatici: mittente, destinatari, copia (CC) e oggetto di ciascuno. Riconoscibili in ogni pagina dal pulsante verde con il megafono.</p>
        <div className="grid gap-2.5" data-testid="funnels-overview-list">
          {FUNNELS.map((f) => {
            const dyn = funnelStatus ? funnelStatus[f.key] : undefined;
            const stato = f.perRow ? "row" : dyn === undefined ? "loading" : dyn === null ? "unknown" : dyn.enabled ? "on" : "off";
            const dest = (dyn && dyn.destinatari) || f.destinatari || "—";
            const cc = (dyn && dyn.cc) || f.cc || "—";
            const ogg = (dyn && dyn.oggetto) || f.oggetto || "—";
            const Field = ({ label, value }) => (
              <div className="min-w-0">
                <div className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/70">{label}</div>
                <div className="truncate text-[11px] text-foreground/90" title={value}>{value}</div>
              </div>
            );
            return (
              <div key={f.key} className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`funnel-overview-row-${f.key}`}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 font-heading text-sm font-bold text-emerald-600 dark:text-emerald-400"><Megaphone className="h-4 w-4" /> {f.nome}</span>
                  <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-muted-foreground">{f.pagina}</span>
                  <span className="ml-auto">
                    {stato === "row" ? <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-muted-foreground">Per commessa</span>
                      : stato === "loading" ? <span className="text-[10px] text-muted-foreground">…</span>
                      : stato === "unknown" ? <span className="text-[10px] text-muted-foreground">—</span>
                      : stato === "on" ? <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-semibold text-green-400">Attivo</span>
                      : <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-muted-foreground">Spento</span>}
                  </span>
                </div>
                <p className="mb-2.5 text-[11px] text-muted-foreground">{f.descr}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-4">
                  <Field label="Mittente" value={f.mittente || "—"} />
                  <Field label="Destinatari" value={dest} />
                  <Field label="In copia (CC)" value={cc} />
                  <Field label="Oggetto" value={ogg} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="order-2 p-5" data-testid="email-summary-card">
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={() => setShowRecap((s) => !s)} className="flex flex-1 items-center justify-between font-heading text-lg font-bold" data-testid="toggle-recap-btn">
            <span className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Riepilogo mail automatiche</span>
            <span className="flex items-center gap-1 text-xs font-medium text-primary"><ChevronDown className={`h-4 w-4 transition-transform ${showRecap ? "rotate-180" : ""}`} />{showRecap ? "Nascondi" : "Mostra riepilogo"}</span>
          </button>
          <Button variant="outline" size="sm" onClick={downloadRecapPdf} disabled={recapPdfBusy} data-testid="download-recap-pdf-btn">
            {recapPdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Scarica PDF
          </Button>
        </div>
        {showRecap && (
          <div className="mt-4 overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full text-xs">
              <thead className="bg-black/60 text-muted-foreground">
                <tr>
                  <th className="p-2 text-left font-medium">#</th>
                  <th className="p-2 text-left font-medium">Mail</th>
                  <th className="p-2 text-left font-medium">Mittente</th>
                  <th className="p-2 text-left font-medium">Destinatari</th>
                  <th className="p-2 text-left font-medium">Obiettivo</th>
                  <th className="p-2 text-center font-medium">AI</th>
                  <th className="p-2 text-center font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {orderedCatalog.map((c, ci) => {
                  const t = st.templates[c.key] || {};
                  const ai = ["michele_ai_daily", "responsabile_ai_report", "agency_ai_report"].includes(c.key);
                  return (
                    <tr key={c.key} className="border-t border-white/5 align-top" data-testid={`summary-row-${c.key}`}>
                      <td className="p-2 font-bold text-primary">#{MAIL_NUM[c.key] || ci + 1}</td>
                      <td className="p-2 text-foreground/90"><div className="font-medium">{c.nome}</div><div className="text-[10px] text-muted-foreground">{t.subject}</div></td>
                      <td className="p-2 text-foreground/90">{t.from_name || st.from_name}</td>
                      <td className="p-2 text-muted-foreground">{c.destinatario_desc || c.destinatari}</td>
                      <td className="p-2 text-muted-foreground">{c.obiettivo_desc}</td>
                      <td className="p-2 text-center">{ai ? <span className="rounded-full bg-primary/15 px-2 py-0.5 font-semibold text-primary">SÌ</span> : <span className="text-muted-foreground">NO</span>}</td>
                      <td className="p-2 text-center">{t.enabled ? <span className="font-semibold text-green-400">Attiva</span> : <span className="text-muted-foreground">Disattivata</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="order-4 p-5" data-testid="email-schedule-card">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold"><CalendarClock className="h-5 w-5 text-primary" /> Prossimi invii programmati</h2>
        {!schedule ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : schedule.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-schedule">Nessuna email automatica attiva. Attiva un'email qui sotto per vederla programmata.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2" data-testid="schedule-list">
            {schedule.map((s, i) => {
              const dt = new Date(s.next);
              const when = dt.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
              return (
                <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2" data-testid={`schedule-item-${i}`}>
                  <div>
                    <div className="text-sm font-medium text-foreground">{mailTitle(s)}</div>
                    <div className="text-[11px] text-muted-foreground">{s.periodo}</div>
                  </div>
                  <span className="whitespace-nowrap rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">{when}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="order-4 p-5" data-testid="email-schedule-history-card">
        <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold"><History className="h-5 w-5 text-primary" /> Ultimi invii programmati — esiti</h2>
        <p className="mb-3 text-[11px] text-muted-foreground">Stato dell'ultima occorrenza <b>già passata</b> di ogni invio automatico. Gli invii futuri sono nel riquadro qui sopra.</p>
        {!schedule ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : schedule.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-schedule-history">Nessun invio automatico attivo.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2" data-testid="schedule-history-list">
            {schedule.map((s, i) => {
              const st = schedStatus(s);
              const I = st.Icon;
              const due = s.last_due ? new Date(s.last_due).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
              return (
                <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2" data-testid={`schedule-history-item-${i}`}>
                  <div>
                    <div className="text-sm font-medium text-foreground">{mailTitle(s)}</div>
                    <div className="text-[11px] text-muted-foreground">Atteso: {due}</div>
                    <div className={`mt-0.5 flex items-center gap-1 text-[11px] ${st.cls}`} data-testid={`schedule-history-status-${i}`}><I className="h-3 w-3" /> {st.text}</div>
                  </div>
                  {(s.last_status === "error" || s.last_status === "retrying") && (
                    <div className="flex flex-col items-end gap-0.5">
                      <Button size="sm" variant="outline" className="h-7" onClick={() => runNow(s.key)} disabled={runningKey === s.key} data-testid={`run-now-${s.key}`} title="Invia subito con i DATI DI OGGI (non ricostruisce i dati della data mancata)">
                        {runningKey === s.key ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />} Invia ora
                      </Button>
                      <span className="text-[10px] text-muted-foreground">usa i dati di oggi</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="order-5 p-5" data-testid="email-logs-card">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><History className="h-5 w-5 text-primary" /> Storico invii</h2>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-primary/15 px-3 py-1 font-semibold text-primary" data-testid="mails-today-count">Oggi: {logs?.count_today ?? 0} ({logs?.count_today_sent ?? 0} riuscite · {logs?.count_today_failed ?? 0} fallite · {logs?.count_today_skipped ?? 0} saltate)</span>
            <span className="rounded-full bg-white/10 px-3 py-1 text-muted-foreground" data-testid="mails-total-count">Totale: {logs?.total ?? 0}</span>
            <span className="rounded-full bg-white/10 px-3 py-1 text-muted-foreground" data-testid="last-dispatch" title="Aggiornato ad ogni ciclo del cron di invio (ogni 5 minuti). Se resta vecchio, il cron non sta girando.">Ultimo controllo invii: {logs?.last_dispatch ? new Date(logs.last_dispatch).toLocaleString("it-IT") : "mai"}</span>
            <Button size="sm" variant={onlyFailed ? "default" : "outline"} className="h-7" onClick={() => setOnlyFailed((v) => !v)} data-testid="logs-filter-failed"><AlertTriangle className="mr-1 h-3.5 w-3.5" /> Solo anomalie</Button>
            <Button size="sm" variant="outline" className="h-7" onClick={resendFailed} disabled={resending || !(logs?.count_today_failed)} data-testid="logs-resend-failed" title="Ritenta l'invio delle email fallite di oggi non ancora consegnate">{resending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />} Reinvia falliti</Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={loadLogs} data-testid="logs-refresh" title="Aggiorna"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        {(() => {
          const rows = (logs?.logs || []).filter((l) => !onlyFailed || l.status !== "sent");
          if (!rows.length) return <p className="text-sm text-muted-foreground" data-testid="no-logs">{onlyFailed ? "Nessun invio fallito di recente. Tutto ok ✅" : "Nessuna email inviata finora. Le email compaiono qui non appena i promemoria/report vengono spediti."}</p>;
          return (
          <div className="max-h-80 overflow-y-auto rounded-lg border border-white/10" data-testid="email-logs-list">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-black/60 text-muted-foreground">
                <tr><th className="p-2 text-left font-medium">Data/ora</th><th className="p-2 text-left font-medium">Tipo</th><th className="p-2 text-left font-medium">Destinatario</th><th className="p-2 text-left font-medium">Esito</th><th className="p-2 text-left font-medium">Consegna / Apertura</th><th className="p-2 text-left font-medium">Motivo</th></tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id} className="border-t border-white/5" data-testid={`log-row-${l.id}`}>
                    <td className="p-2 whitespace-nowrap text-foreground/90">{new Date(l.created_at).toLocaleString("it-IT")}</td>
                    <td className="p-2 text-muted-foreground">{tipoLabel(l.tipo)}</td>
                    <td className="p-2 text-foreground/90">{l.to}</td>
                    <td className="p-2">{l.status === "sent"
                      ? <span className="inline-flex items-center gap-1 text-green-400"><CheckCircle2 className="h-3.5 w-3.5" /> Inviata</span>
                      : <span className="inline-flex items-center gap-1 text-red-400"><XCircle className="h-3.5 w-3.5" /> Fallita</span>}</td>
                    <td className="p-2 whitespace-nowrap" data-testid={`log-delivery-${l.id}`}>{deliveryCell(l)}</td>
                    <td className="p-2" data-testid={`log-motivo-${l.id}`}>{l.status === "sent" ? <span className="text-muted-foreground">—</span> : <span className="text-red-300/90" title={l.error || ""}>{errMotivo(l.error)}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}
      </Card>

      <div className="order-3 flex flex-col gap-4" data-testid="email-content-blocks">
      {orderedCatalog.map((c, ci) => {
        const t = st.templates[c.key] || {};
        const rec = t.recipients || { roles: [], agencies_on: false, agencies: [], emails: [] };
        const perEntity = ["agency_ai_report", "daily_reminders", "agent_activity_todo"].includes(c.key);
        const rolesBlock = (
          <div className="flex flex-wrap gap-4">
            {ROLE_OPTS.map(([r, lbl]) => {
              const on = (rec.roles || []).includes(r);
              const locked = c.key === "responsabile_ai_report" && r === "commercial_manager";
              return (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <Switch checked={locked ? true : on} disabled={locked} onCheckedChange={() => { if (!locked) toggleRole(c.key, r); }} data-testid={`recip-role-${r}-${c.key}`} />
                  <span className={(locked || on) ? "text-foreground" : "text-muted-foreground"}>{lbl}{locked && <span className="ml-1 text-[10px] text-primary">(obbl.)</span>}</span>
                </label>
              );
            })}
          </div>
        );
        const agenciesToggle = (
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={!!rec.agencies_on} onCheckedChange={(v) => setRecip(c.key, { agencies_on: v })} data-testid={`recip-agencies-on-${c.key}`} />
            <span className={rec.agencies_on ? "text-foreground" : "text-muted-foreground"}>Agenzie / Agenti</span>
          </label>
        );
        const agenciesDropdown = rec.agencies_on ? (
          <div className="mt-2">
            <button type="button" onClick={() => setOpenAgencies((m) => ({ ...m, [c.key]: !m[c.key] }))} className="flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`agencies-toggle-${c.key}`}>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openAgencies[c.key] ? "rotate-180" : ""}`} />
              Seleziona agenzie ({(!rec.agencies || rec.agencies.length === 0) ? "tutte" : `${rec.agencies.length}/${(data.agencies || []).length}`})
            </button>
            {openAgencies[c.key] && (
              <div className="mt-2 max-h-56 space-y-1.5 overflow-y-auto rounded-lg border border-white/10 bg-black/40 p-3" data-testid={`agencies-list-${c.key}`}>
                {(data.agencies || []).map((ag) => (
                  <label key={ag.id} className="flex items-center gap-2 text-xs">
                    <Switch checked={agencyChecked(c.key, ag.id)} onCheckedChange={() => toggleAgency(c.key, ag.id)} data-testid={`agency-${ag.id}-${c.key}`} />
                    <span className={agencyChecked(c.key, ag.id) ? "text-foreground" : "text-muted-foreground"}>{ag.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : null;
        const emailsBlock = (
          <div className="mt-3 space-y-1.5">
            <Label className="text-xs">Email aggiuntive (separate da virgola)</Label>
            <Input value={recipEmailsValue(c.key)} onChange={(e) => setEmailText((m) => ({ ...m, [c.key]: e.target.value }))} onBlur={() => commitEmails(c.key)} placeholder="es. mario@azienda.it" data-testid={`recip-emails-${c.key}`} />
          </div>
        );
        return (
          <Card key={c.key} className="relative overflow-hidden border-2 border-primary p-0" data-testid={`email-card-${c.key}`}>
            <div className="bg-primary px-4 py-2 font-heading text-sm font-black uppercase leading-snug tracking-wide text-primary-foreground" data-testid={`email-label-${c.key}`}>#{MAIL_NUM[c.key] || ci + 1} {c.nome}</div>
            <div className="p-5">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.quando}</span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {c.destinatari}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {perEntity && (agents?.length > 0) && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground">Prova per:</span>
                    <Select value={testTarget[c.key] || "__net__"} onValueChange={(v) => setTestTarget((m) => ({ ...m, [c.key]: v === "__net__" ? "" : v }))}>
                      <SelectTrigger className="h-8 w-48 text-xs" data-testid={`test-target-${c.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__net__">Direzione (vista rete)</SelectItem>
                        {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.agency_name ? ` · ${a.agency_name}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button size="sm" variant="outline" onClick={() => sendTest(c.key)} disabled={testKey === c.key} data-testid={`test-send-${c.key}`}>
                  {testKey === c.key ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />} Invia prova
                </Button>
                <label className="flex items-center gap-2 text-sm">
                  <span className={t.enabled ? "text-green-400" : "text-muted-foreground"}>{t.enabled ? "Attiva" : "Disattivata"}</span>
                  <Switch checked={!!t.enabled} onCheckedChange={(v) => setTpl(c.key, "enabled", v)} data-testid={`toggle-${c.key}`} />
                </label>
              </div>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">Contenuto automatico: {c.contenuto}</p>
            {(c.destinatario_desc || c.obiettivo_desc) && (
              <div className="mb-3 space-y-1 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`desc-${c.key}`}>
                {c.destinatario_desc && (
                  <p className="text-[11px] text-muted-foreground"><span className="font-semibold text-primary">Destinatario:</span> {c.destinatario_desc}</p>
                )}
                {c.obiettivo_desc && (
                  <p className="text-[11px] text-muted-foreground"><span className="font-semibold text-primary">Obiettivo:</span> {c.obiettivo_desc}</p>
                )}
              </div>
            )}
            <div className="space-y-3">
              {c.time && (
                <div className="flex flex-wrap items-center gap-3" data-testid={`sendtime-wrap-${c.key}`}>
                  {c.dow && (
                    <>
                      <Label className="text-xs">Giorno</Label>
                      <Select value={String(t.send_dow ?? 0)} onValueChange={(v) => setTpl(c.key, "send_dow", parseInt(v, 10))}>
                        <SelectTrigger className="h-9 w-36 text-xs" data-testid={`senddow-${c.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DOW_OPTS.map(([val, lbl]) => <SelectItem key={val} value={String(val)}>{lbl}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  <Label className="text-xs">Orario di invio</Label>
                  <Input type="time" className="w-32" value={t.send_time || ""} onChange={(e) => setTpl(c.key, "send_time", e.target.value)} data-testid={`sendtime-${c.key}`} />
                  {c.dom && (
                    <>
                      <Label className="text-xs">Giorno del mese</Label>
                      <Select value={String(t.send_dom ?? 1)} onValueChange={(v) => setTpl(c.key, "send_dom", parseInt(v, 10))}>
                        <SelectTrigger className="h-9 w-20 text-xs" data-testid={`senddom-${c.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  <span className="text-[11px] text-muted-foreground">fuso orario Italia</span>
                  <label className="flex items-center gap-2 text-xs" data-testid={`skipwe-wrap-${c.key}`}>
                    <Switch checked={!!t.skip_weekend} onCheckedChange={(v) => setTpl(c.key, "skip_weekend", v)} data-testid={`skip-weekend-${c.key}`} />
                    <span className="text-muted-foreground">Escludi sabato e domenica</span>
                  </label>
                </div>
              )}
              {c.cadence && (
                <div className="flex flex-wrap items-center gap-3" data-testid={`cadence-wrap-${c.key}`}>
                  <Label className="text-xs">Cadenza</Label>
                  <Select value={t.cadence || "biweekly"} onValueChange={(v) => setTpl(c.key, "cadence", v)}>
                    <SelectTrigger className="h-9 w-44 text-xs" data-testid={`cadence-${c.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Settimanale</SelectItem>
                      <SelectItem value="biweekly">Ogni 2 settimane</SelectItem>
                      <SelectItem value="monthly">Mensile</SelectItem>
                    </SelectContent>
                  </Select>
                  {(t.cadence || "biweekly") !== "monthly" ? (
                    <>
                      <Label className="text-xs">Giorno</Label>
                      <Select value={String(t.send_dow ?? 0)} onValueChange={(v) => setTpl(c.key, "send_dow", parseInt(v, 10))}>
                        <SelectTrigger className="h-9 w-32 text-xs" data-testid={`cad-dow-${c.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{DOW_OPTS.map(([val, lbl]) => <SelectItem key={val} value={String(val)}>{lbl}</SelectItem>)}</SelectContent>
                      </Select>
                    </>
                  ) : (
                    <>
                      <Label className="text-xs">Giorno del mese</Label>
                      <Select value={String(t.send_dom ?? 1)} onValueChange={(v) => setTpl(c.key, "send_dom", parseInt(v, 10))}>
                        <SelectTrigger className="h-9 w-20 text-xs" data-testid={`cad-dom-${c.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-60">{Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </>
                  )}
                </div>
              )}
              {!["michele_ai_daily", "responsabile_ai_report", "agency_ai_report", "daily_reminders", "weekly_report", "agent_activity_todo"].includes(c.key) && (
                <label className="flex items-center gap-2 text-sm" data-testid={`aireco-wrap-${c.key}`}>
                  <Switch checked={t.ai_reco !== false} onCheckedChange={(v) => setTpl(c.key, "ai_reco", v)} data-testid={`ai_reco-${c.key}`} />
                  <span className={t.ai_reco !== false ? "text-foreground" : "text-muted-foreground"}>Aggiungi raccomandazioni AI in fondo all'email</span>
                </label>
              )}
              {c.options === "freq" && (
                <div className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`freq-options-${c.key}`}>
                  <div className="mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">Frequenze di invio</div>
                  <div className="flex flex-wrap gap-4">
                    {[["freq_daily", "Giornaliera"], ["freq_weekly", "Settimanale"], ["freq_monthly", "Mensile"], ["copy_to_michele", "Copia a Michele"]].map(([f, lbl]) => (
                      <label key={f} className="flex items-center gap-2 text-sm">
                        <Switch checked={t[f] !== false} onCheckedChange={(v) => setTpl(c.key, f, v)} data-testid={`${f}-${c.key}`} />
                        <span className={t[f] !== false ? "text-foreground" : "text-muted-foreground"}>{lbl}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-white/10 bg-black/20 p-4" data-testid={`recipients-${c.key}`}>
                <div className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-primary"><Users className="h-3.5 w-3.5" /> Mittente e destinatari</div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nome mittente (visualizzato)</Label>
                    <Input value={t.from_name || ""} onChange={(e) => setTpl(c.key, "from_name", e.target.value)} placeholder={st.from_name} data-testid={`from-name-${c.key}`} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Rispondi a (reply-to, opzionale)</Label>
                    <Input value={t.reply_to || ""} onChange={(e) => setTpl(c.key, "reply_to", e.target.value)} placeholder="es. commerciale@torrisolare.it" data-testid={`reply-to-${c.key}`} />
                  </div>
                </div>
                {perEntity ? (
                  <div className="mt-3 space-y-3">
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-primary">Report dedicato — {c.key === "agency_ai_report" ? "agenzie" : "agenti"}</div>
                      <div className="mb-2 text-[11px] text-muted-foreground">{c.key === "agency_ai_report" ? "Ogni agenzia selezionata riceve la propria valutazione." : "Ogni agente riceve solo i propri dati (agenda / clienti personali)."}</div>
                      {agenciesToggle}
                      {agenciesDropdown}
                      {["daily_reminders", "agent_activity_todo"].includes(c.key) && (
                        <label className="mt-3 flex items-center gap-2 border-t border-white/10 pt-3 text-sm">
                          <Switch checked={!!rec.manager_personal} onCheckedChange={(v) => setRecip(c.key, { manager_personal: v })} data-testid={`manager-personal-${c.key}`} />
                          <span className={rec.manager_personal ? "text-foreground" : "text-muted-foreground"}>Includi Responsabile Commerciale (i suoi clienti direzionali)</span>
                        </label>
                      )}
                    </div>
                    <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">In copia / riepilogo — direzione</div>
                      <div className="mb-2 text-[11px] text-muted-foreground">Ricevono il riepilogo di rete aggregato (non il dettaglio personale).</div>
                      {rolesBlock}
                      {emailsBlock}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3">
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Destinatari — ricevono tutti la stessa email</div>
                    {rolesBlock}
                    <div className="mt-3">{agenciesToggle}</div>
                    {agenciesDropdown}
                    {emailsBlock}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-white/10 bg-black/20 p-4" data-testid={`content-fields-${c.key}`}>
              <div className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-primary">Oggetto e testo introduttivo</div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Oggetto dell'email</Label>
                  <Input value={t.subject || ""} onChange={(e) => setTpl(c.key, "subject", e.target.value)} data-testid={`subject-${c.key}`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Testo introduttivo</Label>
                  <Input value={t.intro || ""} onChange={(e) => setTpl(c.key, "intro", e.target.value)} data-testid={`intro-${c.key}`} />
                </div>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">Per vedere il contenuto reale usa <span className="font-semibold text-primary">Invia prova</span> qui sopra: scegli il destinatario e la mail vera arriva alla tua casella.</div>
            </div>
            </div>
          </Card>
        );
      })}
      </div>
    </div>
  );
}
